import { useState, type FormEvent } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, Input, Label, Subtitle, Title } from '../components/ui'
import { errorMessage } from '../lib/format'

export function MfaRecoverPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null)
  const [otpauth, setOtpauth] = useState<string | null>(null)
  const [totp, setTotp] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onStart(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const res = await api.mfaRecoverStart(email.trim(), recoveryCode.trim())
      setRecoveryToken(res.recovery_token)
      setOtpauth(res.otpauth_uri)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault()
    if (!recoveryToken) return
    setError(null)
    setBusy(true)
    try {
      const res = await api.mfaRecoverConfirm(recoveryToken, totp.trim())
      setCodes(res.recovery_codes)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <PageShell homeTo="/login">
      <Card className="mx-auto max-w-lg">
        <Title>Recover MFA</Title>
        <Subtitle>Use a one-time recovery code, then enroll a new authenticator.</Subtitle>
        {codes ? (
          <div className="space-y-4">
            <Alert tone="success">New MFA enrolled. Save these recovery codes.</Alert>
            <ul className="grid grid-cols-1 gap-2 rounded-lg bg-slate-100 p-3 font-mono text-sm sm:grid-cols-2">
              {codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button type="button" className="w-full" onClick={() => navigate('/login')}>
              Back to sign in
            </Button>
          </div>
        ) : !recoveryToken ? (
          <form className="space-y-4" onSubmit={onStart}>
            {error ? <Alert>{error}</Alert> : null}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="recovery">Recovery code</Label>
              <Input
                id="recovery"
                required
                value={recoveryCode}
                onChange={(ev) => setRecoveryCode(ev.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Starting…' : 'Start recovery'}
            </Button>
            <p className="text-center text-sm">
              <Link to="/login">Back to sign in</Link>
            </p>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={onConfirm}>
            {error ? <Alert>{error}</Alert> : null}
            {otpauth ? (
              <div className="flex justify-center">
                <div className="rounded-lg border p-3">
                  <QRCodeSVG value={otpauth} size={160} />
                </div>
              </div>
            ) : null}
            <div>
              <Label htmlFor="totp">New authenticator code</Label>
              <Input
                id="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={totp}
                onChange={(ev) => setTotp(ev.target.value)}
                className="text-center text-lg tracking-widest"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Confirming…' : 'Confirm new MFA'}
            </Button>
          </form>
        )}
      </Card>
    </PageShell>
  )
}
